"use client";

import React, { useEffect, useId, useRef, useState } from "react";
/* ⚠ Aliased. The parser exports a type called `Element`, which would shadow the
   DOM's global `Element` — and this file needs the global one as a VALUE, for
   the `instanceof` narrowing the click listener depends on. */
import { domToReact, type DOMNode, type Element as ParsedElement } from "html-react-parser-v6";
import { Button } from "../../atoms/button/button";
import { Link } from "../../atoms/link/link";
import { Text, type TextElement } from "../../atoms/text/text";
import { Modal } from "../../molecules/modal/modal";
import { LegalModalBackToTop, scrollPortOf } from "./legal-modal-back-to-top.client";
import { REFUSED, fetchLegalDocument } from "./legal-content";
import { parseLegalDocument, type LegalDocument, type TocEntry } from "./toc";
import styles from "./legal-modal.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * Legal modal — a CMS legal document in a dialog, indexed by its own headings.
 *
 * ONE per page, not one per document. A link in rich text carrying
 * `data-legal-modal` opens it, and the link's own `href` is the document to
 * fetch — so a page can point any number of links at any number of documents
 * without naming or configuring any of them.
 *
 * ⚠ A client component, which a legal document leaves no choice about: the
 * authored href carries no host, so only a browser can resolve it, and the
 * document is not known until someone clicks.
 *
 * ⚠ Nothing fires an analytics event from here. GTM's Link Click trigger reads
 * the attributes the Link atom renders, and the Modal reports its own page view
 * — firing on top of those is what double-counts every index click.
 *
 * Design reference and every divergence: design-conflicts/legal-modal.mdx.
 */

/** The attribute a trigger carries. Its `href` supplies the document. */
export const TRIGGER_ATTRIBUTE = "data-legal-modal";

const TRIGGER_SELECTOR = `a[${TRIGGER_ATTRIBUTE}]`;

/**
 * Parsed documents by URL, at module scope so it survives closing the dialog.
 * Reopening the same terms then costs nothing — no request, no loading state.
 *
 * Never evicted, deliberately: it is bounded by the number of DISTINCT legal
 * URLs a page links to, which is a handful, and a reader who opens the terms
 * twice is the case it exists for. Revisit if a page ever links to enough
 * documents for the parsed trees to matter.
 */
const cache = new Map<string, LegalDocument>();

export interface LegalModalProps {
  /**
   * Announced name for the dialog. Required because the document's own title is
   * not known until it arrives, so this is the name while it loads and whenever
   * the document carries no title.
   */
  aria_label: string;
  /** Accessible name for the index. */
  toc_label?: string;
  /** Wording on the return-to-top control. */
  back_to_top_label?: string;
  /**
   * Shown while the document is being fetched. Defaults to the production
   * component's wording, which is not business-approved copy.
   */
  loading_text?: string;
  /** Title of the unavailable state, shown when the document cannot be read. */
  unavailable_title?: string;
  /** Wording under that title. */
  unavailable_text?: string;
  /** Wording on the unavailable state's button, which closes the dialog. */
  close_label?: string;
}

/* The error frame's own copy (16565:36583). Not UNAVAILABLE_TEXT, which stays
   In-Page Legal's wording. */
const UNAVAILABLE_TITLE = "We couldn’t load the legal details";
const UNAVAILABLE_BODY = "Something went wrong on our end. Please try again.";

/**
 * Authored heading level → the element it renders as, verbatim.
 *
 * ⚠ Depends on the Modal being given `titleAs="h1"`: the dialog's title and the
 * document's top-level sections are peers in the heading tree. On the Modal's
 * default `h2`, every section heading outranks the dialog's own name.
 *
 * ⚠ Own-property lookup rather than a bare index, for the reason given in
 * `toc.ts` — `<constructor>` would otherwise answer truthy.
 */
const HEADING_ELEMENTS: Record<string, TextElement> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4"
};

function headingElement(name: string): TextElement | undefined {
  return Object.prototype.hasOwnProperty.call(HEADING_ELEMENTS, name) ?
    HEADING_ELEMENTS[name] :
    undefined;
}

/** Same structural test as `toc.ts`, and for the same reason — see that file. */
function isElement(node: DOMNode): node is ParsedElement {
  const { type } = (node as { type?: string });
  return type === "tag" || type === "script" || type === "style";
}

function attr(node: ParsedElement, name: string): string {
  const value = node.attribs?.[name];
  return typeof value === "string" ? value : "";
}

function refused(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(REFUSED, name);
}

/**
 * ⚠ React passes an unrecognised lower-case `onerror` through as a real
 * attribute, so it becomes a live inline handler. Everything this map does not
 * rewrite renders with its authored attributes, which is where that would slip
 * in — so they come off before the default rendering sees them.
 */
function stripHandlers(node: ParsedElement): void {
  const { attribs } = node;
  if (!attribs) {
    return;
  }
  Object.keys(attribs)
    .filter(key => /^on/i.test(key))
    .forEach(key => {
      delete attribs[key];
    });
}

/**
 * The reporting vocabulary, fixed rather than taken from a prop.
 *
 * ⚠ The production component hardcodes `modal_legal_toc` too. A per-instance
 * position would split one figure in the tag report across every page that
 * carries a legal link, which is the opposite of what the dimension is for.
 *
 * `MODAL_NAME` reaches the Modal, which derives `modal_legal` from it for the
 * close control and for the page view it raises on open.
 */
const TOC_LINK_POSITION = "modal_legal_toc";
const BODY_LINK_POSITION = "modal_legal";
const MODAL_NAME = "legal";

/**
 * The page the reader is on, without any fragment.
 *
 * ⚠ An index entry reports the FULL destination, not the bare `#id` its href
 * carries. GTM reads `data-linkdestination` straight through, and every other
 * same-page link on the site reports an absolute URL — a lone fragment lands
 * in the tag report as a value nothing else matches.
 */
function pageUrl(): string {
  return typeof window === "undefined" ? "" : window.location.href.split("#")[0];
}

function TocList({
  entries,
  depth,
  linkPosition
}: {
  entries: TocEntry[];
  depth: number;
  linkPosition?: string;
}) {
  const page = pageUrl();

  return (
    <ul className={depth === 0 ? styles.list : `${styles.list} ${styles.nested}`}>
      {entries.map(entry => (
        <li key={entry.id}>
          {/*
            ⚠ `low` at EVERY depth, stated rather than left to the default.
            `contrast` is a colour axis, not a weight one — `high` paints
            near-white — so carrying depth on it would give one list two
            colours. The frames draw one blue throughout; depth is the indent.
          */}
          <Link
            href={`#${entry.id}`}
            contrast="low"
            linkPosition={linkPosition}
            linkDestination={`${page}#${entry.id}`}
          >
            {entry.label}
          </Link>
          {entry.children.length > 0 ? (
            <TocList
              entries={entry.children}
              depth={depth + 1}
              linkPosition={linkPosition}
            />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function LegalModalImpl({
  aria_label,
  toc_label = "Table of contents",
  back_to_top_label = "Back to top",
  /* Verbatim from the production component. Not business-approved copy —
     matched so behaviour does not change on the way over, and overridable. */
  loading_text = "Loading...",
  unavailable_title = UNAVAILABLE_TITLE,
  unavailable_text = UNAVAILABLE_BODY,
  close_label = "Close"
}: LegalModalProps) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [document_, setDocument] = useState<LegalDocument | null>(null);
  const [failed, setFailed] = useState(false);
  /* Bumped on every opening, so reopening the same link refetches a document
     whose first attempt failed. */
  const [attempt, setAttempt] = useState(0);

  const contentRef = useRef<HTMLDivElement>(null);
  const anchorId = `${useId()}-top`;

  useEffect(() => {
    function onDocumentClick(event: MouseEvent): void {
      /* Something nearer the click already handled it. */
      if (event.defaultPrevented) {
        return;
      }

      /* ⚠ Leave modified and non-primary clicks to the browser. Opening terms
         in a new tab is an affordance readers use on exactly these links. */
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const { target } = event;
      if (!(target instanceof Element)) {
        return;
      }

      /* `closest`, so a click on wording inside the anchor still resolves. */
      const trigger = target.closest(TRIGGER_SELECTOR);
      if (!trigger || !trigger.closest("[data-dds-root]")) {
        return;
      }

      /* No href is nothing to fetch. Left alone rather than opening an empty
         dialog, so a mis-authored link stays visibly broken. */
      const href = trigger.getAttribute("href");
      if (!href) {
        return;
      }

      event.preventDefault();

      /* ⚠ The state for this opening is set HERE rather than in the fetch
         effect, for two reasons. An effect runs after paint, so a reader
         switching documents would see the previous one for a frame; and
         `setUrl` with an unchanged href is a no-op, so an effect keyed on the
         url alone would never re-run and a failed document could never be
         retried. `attempt` is what makes reopening the same link refetch. */
      setDocument(cache.get(href) ?? null);
      setFailed(false);
      setUrl(href);
      setAttempt(n => n + 1);
      setOpen(true);
    }

    /* ⚠ On `document`, not a wrapper: React attaches its listeners to the root
       container, so a listener there fires BEFORE React's and would steal
       clicks. On `document` it fires after, which is what makes the
       `defaultPrevented` check above mean anything. */
    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, []);

  /* Fetching only. What the dialog shows for this opening is already set by the
     click handler, including a cache hit. */
  useEffect(() => {
    if (url === null || cache.has(url)) {
      return undefined;
    }

    /* ⚠ Discards a result that arrives after the reader moved on — closed the
       dialog, or opened different terms. Without it a slow response paints into
       a dialog showing something else. */
    let live = true;

    fetchLegalDocument(url).then(html => {
      if (!live) {
        return;
      }
      if (html === null) {
        setFailed(true);
        return;
      }
      const parsed = parseLegalDocument(html);
      cache.set(url, parsed);
      setDocument(parsed);
    });

    return () => {
      live = false;
    };
  }, [url, attempt]);

  /* One dialog serves every document, so its scroll region carries the last
     one's offset.
     ⚠ The REGION is reset, not a node scrolled into view. The anchor sits below
     the index, so scrolling to it would hide the index on every opening — the
     component's headline feature. */
  useEffect(() => {
    if (!open) {
      return;
    }
    const port = scrollPortOf(contentRef.current);
    if (port) {
      port.scrollTop = 0;
    }
  }, [open, url, document_]);

  /* Recursive, so a heading or an anchor nested in the CMS markup is mapped
     too, not just a top-level one. */
  const options = {
    replace: (node: DOMNode) => {
      if (!isElement(node)) {
        return undefined;
      }

      if (refused(node.name)) {
        return <></>;
      }
      stripHandlers(node);

      const children = node.children as DOMNode[];

      const as = headingElement(node.name);
      if (as) {
        if (children.length === 0) {
          return undefined;
        }
        return (
          <Text as={as} variant="body-2" weight="bold" id={attr(node, "id")}>
            {domToReact(children, options)}
          </Text>
        );
      }

      /*
        ⚠ Mapped so it carries no margin. Preflight is off, so a raw `<p>` keeps
        the browser's `margin-block: 1em`, and under a flex `gap` those margins
        add rather than collapse — 16 + 16 + 16 reads as 48px. Rendering each
        paragraph AS a Text leaves the container the only source of rhythm.
      */
      if (node.name === "p") {
        if (children.length === 0) {
          return undefined;
        }
        return <Text as="p" variant="body-2">{domToReact(children, options)}</Text>;
      }

      if (node.name === "a") {
        const href = attr(node, "href");

        /* ⚠ Neither branch may fall through to the default rendering, which
           emits the authored `<a>` untouched: an anchor with an href and no
           wording is a FOCUSABLE EMPTY LINK, and one without an href is not a
           link at all. Both are dropped rather than mapped — `Link` requires an
           href, so there is nothing to map a hrefless anchor to. */
        if (children.length === 0) {
          return <></>;
        }
        if (href === "") {
          return <>{domToReact(children, options)}</>;
        }

        return (
          <Link
            href={href}
            linkPosition={BODY_LINK_POSITION}
            newWindow={attr(node, "target") === "_blank"}
          >
            {domToReact(children, options)}
          </Link>
        );
      }

      return undefined;
    }
  };

  /**
   * ⚠ THREE states, not two. `resolved` means the fetch came back; `ready`
   * means it came back with something to read. A document that parses to no
   * renderable nodes — one carrying only a `<title>`, which the feed can
   * legitimately return — is resolved but not ready, and must fall to the
   * unavailable wording. Deriving "still loading" from `ready` alone left that
   * document showing "Loading..." for ever, which is the production defect this
   * component was written to avoid.
   */
  const resolved = document_ !== null;
  const ready = resolved && document_.nodes.length > 0;
  const loading = !resolved && !failed;
  const unavailable = !ready && !loading;

  const hasIndex = ready && document_.toc.length > 0;

  /**
   * ⚠ Goes FIRST in the scroll region, which is why it moves with the index.
   * It answers "has the reader moved", so below the index a long list on a
   * short viewport leaves it out of view at rest and the return control appears
   * before anyone has scrolled. Inside the `<nav>` rather than above it because
   * `.body` is a flex column with a gap, and a zero-size child of its own would
   * add one above the index.
   */
  const sentinel = <div id={anchorId} className={styles.anchor} aria-hidden="true" />;

  const body = (
    <>
      {hasIndex ? (
        <nav aria-label={toc_label}>
          {sentinel}
          <TocList entries={document_.toc} depth={0} linkPosition={TOC_LINK_POSITION} />
        </nav>
      ) : null}

      <div className={styles.content} ref={contentRef}>
        {/* Only beside a document: as a flex item it would add the content's
            gap above the loading and unavailable wording. */}
        {ready && !hasIndex ? sentinel : null}

        {ready ? domToReact(document_.nodes, options) : null}

        {/* ⚠ A live region, because the wait has to be ANNOUNCED and `Text`
            forwards no unknown props — an `aria-*` attribute passed to it is
            silently dropped. `role="status"` carries an implicit polite
            announcement, which is the conventional pattern for this. */}
        {ready ? null : (
          <div role="status">
            <Text variant={loading ? "body-2" : "body-1"}>
              {loading ? loading_text : unavailable_text}
            </Text>
          </div>
        )}

        {/* ⚠ Gated on `open` as well as `ready`, so closing UNMOUNTS it. Its
            "reader has moved" state would otherwise survive a close — a closed
            dialog reports the anchor as not intersecting — and reopening a
            cached document would flash the control before the observer caught
            up. */}
        {open && ready ? (
          <LegalModalBackToTop
            anchorId={anchorId}
            label={back_to_top_label}
            linkPosition={TOC_LINK_POSITION}
          />
        ) : null}
      </div>

      {/* Outside the content, so the Modal body's own gap spaces it. ⚠ Not the
          Modal's CTA, which renders only primary or secondary — the frame
          draws a tonal button. */}
      {unavailable ? (
        <Button
          className={styles.closeAction}
          variant="tonal"
          size="medium"
          onClick={() => setOpen(false)}
        >
          {close_label}
        </Button>
      ) : null}
    </>
  );

  const heading = ready ? document_.title : unavailable ? unavailable_title : "";
  const onClose = () => setOpen(false);

  /*
    ⚠ Two Modal calls, not duplication to tidy away. Its accessible name is a
    discriminated union — visible `title` OR `ariaLabel`, never both — and
    TypeScript cannot narrow a union through a JSX spread. Branching keeps the
    union honest; the alternative is a cast that defeats it.
  */
  return heading ? (
    <Modal
      open={open}
      onClose={onClose}
      modalName={MODAL_NAME}
      title={heading}
      titleAs="h1"
      className={unavailable ? styles.unavailable : undefined}
    >
      {body}
    </Modal>
  ) : (
    <Modal
      open={open}
      onClose={onClose}
      modalName={MODAL_NAME}
      ariaLabel={aria_label}
    >
      {body}
    </Modal>
  );
}
export const LegalModal = withSafeRender(LegalModalImpl, "LegalModal");

export default LegalModal;
